import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Whether this dialog pinned the page. A dialog opened inside another open one
   * (a sheet inside the bag) finds the page already pinned and leaves it to the
   * outer dialog: unpinning it on close would scroll the page behind the still-
   * open bag to the top.
   */
  #ownsScrollLock = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    // Shown already by an inline script, before this component loaded (the bag
    // on return from the app's checkout, sections/includ-cart-drawer-v2.liquid):
    // lock the page and listen as for any other open.
    const early = dialog.hasAttribute('data-early');
    if (dialog.open && !early) return;
    dialog.removeAttribute('data-early');

    // PERF: lazy-render dialog contents.
    // Liquid renders <dialog ref="dialog"> empty + a sibling
    // <template data-dialog-content>. Clone the template into the dialog
    // before showModal so the live DOM stays small until first interaction.
    this.#cloneLazyContent();

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    this.#ownsScrollLock = document.body.style.position !== 'fixed';

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (this.#ownsScrollLock) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      if (!dialog.open) dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Clones <template data-dialog-content> into the <dialog>.
   *
   * Only clones if the dialog is empty AND the template still exists. After
   * a Shopify section morph (e.g., filter/sort apply), the dialog may have
   * been re-rendered with the empty + template structure — this re-runs.
   * If the dialog already has content (no morph since last open), the stray
   * template (if any) is just removed without re-cloning to avoid duplication.
   */
  #cloneLazyContent() {
    const template = this.querySelector(':scope > template[data-dialog-content]');
    if (!(template instanceof HTMLTemplateElement)) return;
    const { dialog } = this.refs;
    if (dialog.children.length > 0) {
      // Dialog already populated; remove the stray template (morph re-introduced
      // it but our existing content is fine).
      template.remove();
      return;
    }
    dialog.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.#ownsScrollLock) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // A click in a dialog nested inside this one (a sheet over the bag) belongs
    // to that dialog: its backdrop lies outside this dialog's box on desktop.
    const inner = event.target instanceof Element ? event.target.closest('dialog') : null;
    if (inner && inner !== dialog) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation(); // a sheet inside the bag closes alone, not the bag with it
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
